// src/onboarding/constants.js
// Phase 37 — extracted from src/App.jsx (Plan 37-03).
//
// Scope: static config for the onboarding wizard.
// Public exports: ONBOARD_GARDEN_SPACES, ONBOARD_EXPERIENCE, ONB_SKIES,
//                 ONB_SKIES_DARK, ONB_AREA_PATH_FALLBACK, ONB_AREA_PATHS,
//                 ONB_GARDEN_SPACE_TO_AREA_LABEL, ONB_SPOTLIGHT_FALLBACKS,
//                 ONBOARD_PRESET_AREAS, GARDEN_SPACE_TO_AREA_LABEL,
//                 GARDEN_SPACE_TO_CUSTOM_AREA.
// Module state: none (all consts).

// Onboarding chip option sets (exact labels/values per UI-SPEC Copywriting Contract)
// Garden-space presets (multi-select per 21-04 gap closure; replaces legacy single "Garden type")
// Phase 31.1 Plan 04 — UAT Gap 6a (2026-04-20): reordered front-first, greenhouse
// before allotment, matching how a user thinks about their home garden.
export const ONBOARD_GARDEN_SPACES = [
  { value: "front-garden", label: "Front garden" },
  { value: "back-garden",  label: "Back garden" },
  { value: "balcony",      label: "Balcony" },
  { value: "greenhouse",   label: "Greenhouse" },
  { value: "allotment",    label: "Allotment" },
];
export const ONBOARD_EXPERIENCE = [
  { value: "beginner",    label: "Just starting out" },
  { value: "some",        label: "Some experience" },
  { value: "experienced", label: "Experienced" },
];

// Phase 31.3 D-19 — onboarding visual constants ported from
// /tmp/design-extract/tend-design-system/project/onboarding/scenes.jsx
// and screens3-4.jsx. Module-level so all onboarding screens share the same
// source of truth. DO NOT duplicate these in src/onboarding.css.
export const ONB_SKIES = [
  { from: "#dce7ea", to: "#f0ede6" }, // step 1 — soft misty dawn
  { from: "#d8e4e8", to: "#f1efe6" }, // step 2 — morning clears
  { from: "#cee0e6", to: "#f4f0e6" }, // step 3 — warming
  { from: "#c6dbe3", to: "#f7f1e6" }, // step 4 — mid-morning
  { from: "#bcd3dc", to: "#faf4e8" }, // step 5 — late morning
  { from: "#b8cfd8", to: "#fdf7ec" }, // step 6 — midday, warmest
];

// Phase 31.3 Gap 4B — dark-mode sky palette. The six light-mode ONB_SKIES
// values are calibrated for light surfaces; in dark mode the app's text
// tokens (--c-heading #b0c8b0, --c-text-3 #9ab89a) render at 1.5–2.5:1
// contrast against the pale-dawn sky — all failing WCAG AA. ONB_SKIES_DARK
// maps the same warming-dawn-to-midday arc onto the dark-surface family
// (#1a2420..#252b25). OnbFrame picks this array when dark mode is active.
export const ONB_SKIES_DARK = [
  { from: "#1a2420", to: "#20281e" }, // step 1 — deep dawn
  { from: "#1c2622", to: "#232a20" }, // step 2 — dawn warming
  { from: "#1e2824", to: "#252c22" }, // step 3 — morning breaks
  { from: "#202a26", to: "#272e24" }, // step 4 — mid-morning
  { from: "#222c28", to: "#293026" }, // step 5 — late morning
  { from: "#242e2a", to: "#2b3228" }, // step 6 — midday, deepest olive warmth
];

// Generic fallback shape for user-added areas not covered by the five
// presets below (Front/Back Garden, Balcony, Allotment, Greenhouse).
// Matches prototype fallback in screens3-4.jsx:65 and :192.
export const ONB_AREA_PATH_FALLBACK = {
  path: "M 80 180 C 70 185, 70 210, 95 215 C 120 220, 140 210, 138 195 C 135 180, 100 170, 80 180 Z",
  lx: 105, ly: 200, color: "rgba(167,139,202,0.25)",
};

export const ONB_AREA_PATHS = {
  "Front Garden": {
    path: "M 30 50 C 20 60, 25 95, 50 100 C 110 108, 150 95, 140 70 C 135 50, 60 40, 30 50 Z",
    lx: 82, ly: 80, color: "rgba(156,184,115,0.28)",
  },
  "Back Garden": {
    path: "M 225 58 C 210 55, 195 70, 200 100 C 205 135, 260 140, 310 128 C 345 118, 345 70, 315 58 C 285 48, 250 52, 225 58 Z",
    lx: 270, ly: 96, color: "rgba(156,184,115,0.32)",
  },
  "Balcony": {
    path: "M 40 160 C 30 165, 30 195, 55 200 C 90 205, 115 198, 110 180 C 105 165, 70 155, 40 160 Z",
    lx: 74, ly: 185, color: "rgba(217,139,95,0.22)",
  },
  "Allotment": {
    path: "M 230 160 C 215 165, 210 205, 240 218 C 280 228, 335 220, 335 190 C 335 165, 280 152, 230 160 Z",
    lx: 280, ly: 195, color: "rgba(139,111,71,0.22)", hatch: true,
  },
  "Greenhouse": {
    path: "M 155 155 C 145 158, 142 185, 158 195 C 185 205, 210 198, 212 180 C 212 162, 180 148, 155 155 Z",
    lx: 180, ly: 180, color: "rgba(200,220,210,0.4)",
  },
};

// Map ONBOARD_GARDEN_SPACES value → ONB_AREA_PATHS key. Front-first order
// preserved from Phase 31.1 UAT-Gap-6a. Used by Plan 04 (Areas / SubSpaces).
export const ONB_GARDEN_SPACE_TO_AREA_LABEL = {
  "front-garden": "Front Garden",
  "back-garden":  "Back Garden",
  "balcony":      "Balcony",
  "greenhouse":   "Greenhouse",
  "allotment":    "Allotment",
};

// Phase 31.3 D-05 — spotlight flower hue pair for OnboardingPlant.
// Brand-palette fallbacks for hue words typed by users into plant names.
// Order: narrow/specific words first so that e.g. "sunflower" wins over "flower".
export const ONB_SPOTLIGHT_FALLBACKS = {
  lavender:   ["#a78bca", "#5a3a70"],
  rose:       ["#e89ab4", "#a84868"],
  sunflower:  ["#e8a547", "#8a5a20"],
  daffodil:   ["#e8d050", "#8a6c20"],
  tulip:      ["#d94a55", "#7a2830"],
  iris:       ["#8060c0", "#3d3070"],
  poppy:      ["#d94a55", "#7a2830"],
  marigold:   ["#e06030", "#8a3018"],
  hydrangea:  ["#7a9bcb", "#3d557a"],
  magnolia:   ["#f0d8e0", "#9b7080"],
  pink:       ["#e89ab4", "#a84868"],
  red:        ["#d94a55", "#7a2830"],
  orange:     ["#e06030", "#8a3018"],
  yellow:     ["#e8a547", "#8a5a20"],
  white:      ["#ece7d3", "#9b957e"],
  blue:       ["#7a9bcb", "#3d557a"],
  purple:     ["#a78bca", "#5a3a70"],
  violet:     ["#a78bca", "#5a3a70"],
  green:      ["#6b8a55", "#3d5a32"],
};

// Step 2 — Areas
export const ONBOARD_PRESET_AREAS = ["Front Garden", "Back Garden", "Balcony", "Allotment"];

// Step 1 gardenSpaces → Step 2 preset area label mapping (plan 21-06 gap closure)
export const GARDEN_SPACE_TO_AREA_LABEL = {
  "back-garden":  "Back Garden",
  "front-garden": "Front Garden",
  "balcony":      "Balcony",
  "allotment":    "Allotment",
  // "greenhouse" has no preset — seeded as a custom area below
};
export const GARDEN_SPACE_TO_CUSTOM_AREA = {
  "greenhouse": "Greenhouse",
};
